import { addressesSeparator, walletTokensPlatformId } from '@/constants';
import { PortfolioAssetO } from '@/types';
import { PortfolioElement } from '@sonarwatch/portfolio-core';
import { computed, ref, Ref, watch } from 'vue';
import { useRoute, onBeforeRouteUpdate } from 'vue-router';
import useAddresses from './useAddresses';
import useAddressesData from './useAddressesData';
import useHiddenElementsValue from './useHiddenElementsValue';
import usePortfolioInfos from './usePortfolioInfos';
import useTokenInfos from './useTokenInfos';

export default function usePortfolio() {
  const excludedChains: Ref<string[]> = ref([]);

  const route = useRoute();

  const rawAddresses = computed(() => {
    const paramsAddresses = route.params.addresses;
    if (!paramsAddresses || typeof paramsAddresses !== 'string') return [];
    return [
      ...new Set(
        paramsAddresses.split(addressesSeparator).map((s) => s.trim()),
      ),
    ];
  });

  onBeforeRouteUpdate(function resetExcludedChainsOnPageChange() {
    excludedChains.value = [];
  });

  const { isInitialized, addressesInfo } = useAddresses({ rawAddresses });

  const { isLoading, elements, refetch, tokenInfos, dataUpdatedAt } =
    useAddressesData(addressesInfo);

  useTokenInfos({ tokenInfos });

  const {
    assetCollectibles,
    assetWalletTokens,
    elementsByNetworkId,
    elementsByPlatformId,
    totalValue,
  } = usePortfolioInfos(elements);

  const { hiddenElementsValue } = useHiddenElementsValue({
    elements,
    assetWalletTokens,
  });

  const isValid = computed(() => {
    return addressesInfo.length > 0;
  });

  const walletInfo = computed(() => {
    let value = 0;
    const assets: PortfolioAssetO[] = [];
    assetWalletTokens.value.forEach((asset) => {
      if (
        excludedChains.value.length === 0 ||
        !excludedChains.value.includes(asset.networkId)
      ) {
        assets.push(asset);
        if (asset.value) value += asset.value;
      }
    });
    return {
      assets,
      value,
    };
  });

  function computeValue(elements: PortfolioElement[], excluded: string[]) {
    let value = 0;
    elements.forEach((element) => {
      if (!element.value) return;
      if (!excluded.includes(element.networkId)) value += element.value;
    });
    return value;
  }

  return {
    dataUpdatedAt,
    portfolioInfo: {
      assetCollectibles,
      assetWalletTokens,
      elementsByNetworkId,
      elementsByPlatformId,
      totalValue,
    },
    isInitialized,
    addressesInfo,
    excludedChains,
    walletInfo,
    computeValue,
    hiddenElementsValue,
    isValid,
    isLoading,
    refetch,
  };
}
